import { useState, useEffect } from 'react';
import { aquaApi } from '../aquaApi';
import { fmtDate } from '../dateUtils';
import DateInput from '../DateInput';

const DEFAULT_CATEGORIES = ['FEED', 'PROBIOTIC', 'MINERAL', 'POND PREP', 'FEED MIX', 'YEAST', 'GEL BINDER', 'ZEOLITE', 'OTHER'];
const PACK_TYPES  = ['BAGs', 'CANs', 'PAKs', 'TINs', 'BOXs'];
const UOMS        = ['Kgs', 'Ltrs', 'Gms'];

const CAT_PALETTE = [
  { bg: '#e6f4ea', color: '#137333' }, { bg: '#e8f0fe', color: '#1a73e8' },
  { bg: '#fce8b2', color: '#b45309' }, { bg: '#fce8e6', color: '#c5221f' },
  { bg: '#f3e8fd', color: '#7b1fa2' }, { bg: '#fff3e0', color: '#e65100' },
  { bg: '#e8f5e9', color: '#2e7d32' }, { bg: '#fbe9e7', color: '#bf360c' },
  { bg: '#e3f2fd', color: '#0277bd' }, { bg: '#fdf6e3', color: '#8d6e00' },
  { bg: '#f9fbe7', color: '#558b2f' }, { bg: '#fce4ec', color: '#ad1457' },
];

const CAT_FIXED = {
  'FEED':       { bg: '#e6f4ea', color: '#137333' },
  'PROBIOTIC':  { bg: '#e8f0fe', color: '#1a73e8' },
  'MINERAL':    { bg: '#fce8b2', color: '#b45309' },
  'POND PREP':  { bg: '#fce8e6', color: '#c5221f' },
  'FEED MIX':   { bg: '#f3e8fd', color: '#7b1fa2' },
  'YEAST':      { bg: '#fff3e0', color: '#e65100' },
  'GEL BINDER': { bg: '#e8f5e9', color: '#2e7d32' },
  'ZEOLITE':    { bg: '#fbe9e7', color: '#bf360c' },
  'OTHER':      { bg: '#f1f3f4', color: '#5f6368' },
};

function catColor(name, allCats) {
  if (CAT_FIXED[name]) return CAT_FIXED[name];
  const idx = allCats.indexOf(name);
  return CAT_PALETTE[idx % CAT_PALETTE.length] || { bg: '#f1f3f4', color: '#5f6368' };
}

const fmtMoney = (v) => v != null && v !== '' ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const fmtQty = (v, uom) => { const d = uom === 'Kgs' || uom === 'Ltrs' ? 3 : 2; return parseFloat(v || 0).toFixed(d); };

const EMPTY_HDR = {
  entryDate: new Date().toISOString().split('T')[0],
  billNo: '',
  vendorName: '',
  paymentAmount: '0',
  notes: '',
};

const EMPTY_ITEM = {
  productName: '',
  category: 'OTHER',
  packSize: '',
  uom: 'Kgs',
  packType: 'BAGs',
  nou: '',
  mrp: '',
  discount: '0',
  frp: '',
  lineAmount: '',
};

function makeItem() { return { ...EMPTY_ITEM }; }

export default function AquaPurchases({ role = 'admin', activeCrops = [] }) {
  const canWrite  = role !== 'readonly';
  const canDelete = role === 'admin';

  // Header form state
  const [hdr, setHdr]           = useState({ ...EMPTY_HDR });
  // Line items state
  const [items, setItems]        = useState([makeItem()]);

  // Ledger
  const [spRows, setSpRows]       = useState([]);
  const [spEditId, setSpEditId]   = useState(null);
  const [spErr, setSpErr]         = useState('');
  const [spLoading, setSpLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('ALL');
  const [expanded, setExpanded]   = useState(new Set());

  // Product names state
  const [products, setProducts]   = useState([]);
  const [pnForm, setPnForm]       = useState({ name: '', category: 'FEED' });
  const [pnEditId, setPnEditId]   = useState(null);
  const [pnErr, setPnErr]         = useState('');
  const [pnSaving, setPnSaving]   = useState(false);

  // Categories state
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [catInput, setCatInput]     = useState('');
  const [catErr, setCatErr]         = useState('');
  const [catSaving, setCatSaving]   = useState(false);

  // Suppliers state
  const [suppliers, setSuppliers] = useState([]);

  // Interest state
  const [interest, setInterest]         = useState([]);
  const [intOpen, setIntOpen]           = useState(false);
  const [intEditId, setIntEditId]       = useState(null);
  const [intForm, setIntForm]           = useState({ interestDate: new Date().toISOString().split('T')[0], vendorName: '', amount: '' });
  const [intErr, setIntErr]             = useState('');
  const [intSaving, setIntSaving]       = useState(false);

  // Stock state
  const [stock, setStock]               = useState([]);
  const [stockLoading, setStockLoading] = useState(false);

  const [tab, setTab] = useState('stock');

  // Cost Impact tab state
  const [ciPonds, setCiPonds]   = useState([]);
  const [ciRates, setCiRates]   = useState({}); // { productName: { vend: '', brr: '' } }
  const [ciLoading, setCiLoading] = useState(false);

  // ── Data loaders ─────────────────────────────────────────────────────────────

  const loadProducts = () =>
    aquaApi.getProductNames().then(d => { if (Array.isArray(d)) setProducts(d.slice().sort((a, b) => a.name.localeCompare(b.name))); }).catch(() => {});

  const loadCategories = () =>
    aquaApi.getCategories().then(d => {
      if (Array.isArray(d) && d.length > 0)
        setCategories(d.map(c => c.name).sort((a, b) => a.localeCompare(b)));
    }).catch(() => {});

  const loadPurchases = () => {
    setSpLoading(true);
    aquaApi.getStockPurchases()
      .then(d => setSpRows(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setSpLoading(false));
  };

  const loadStock = () => {
    setStockLoading(true);
    aquaApi.getOwnerStock()
      .then(d => setStock(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setStockLoading(false));
  };

  const loadInterest = () =>
    aquaApi.getPurchaseInterest().then(d => { if (Array.isArray(d)) setInterest(d); }).catch(() => {});

  useEffect(() => {
    loadProducts(); loadPurchases(); loadStock(); loadCategories(); loadInterest();
    aquaApi.getSuppliers().then(d => { if (Array.isArray(d)) setSuppliers(d.slice().sort((a, b) => a.supplier_name.localeCompare(b.supplier_name))); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab !== 'costimpact') return;
    setCiLoading(true);
    aquaApi.getPonds()
      .then(d => { if (Array.isArray(d)) setCiPonds(d.filter(p => parseFloat(p.area || 0) > 0).sort((a, b) => a.pond_name.localeCompare(b.pond_name))); })
      .catch(() => {})
      .finally(() => setCiLoading(false));
  }, [tab]);

  const openIntAdd = () => {
    setIntEditId(null);
    setIntForm({ interestDate: new Date().toISOString().split('T')[0], vendorName: '', amount: '' });
    setIntErr('');
    setIntOpen(true);
  };

  const openIntEdit = (r) => {
    setIntEditId(r.id);
    setIntForm({
      interestDate: r.interest_date ? r.interest_date.split('T')[0] : new Date().toISOString().split('T')[0],
      vendorName:   r.vendor_name || '',
      amount:       r.amount != null ? String(r.amount) : '',
    });
    setIntErr('');
    setIntOpen(true);
  };

  const handleIntSave = async () => {
    if (!intForm.vendorName.trim()) { setIntErr('Supplier is required'); return; }
    if (!(parseFloat(intForm.amount) > 0)) { setIntErr('Amount must be greater than 0'); return; }
    setIntSaving(true); setIntErr('');
    try {
      if (intEditId) await aquaApi.updatePurchaseInterest(intEditId, intForm);
      else           await aquaApi.createPurchaseInterest(intForm);
      setIntForm({ interestDate: new Date().toISOString().split('T')[0], vendorName: '', amount: '' });
      setIntEditId(null);
      setIntOpen(false);
      loadInterest();
    } catch (e) { setIntErr(e.message); }
    finally { setIntSaving(false); }
  };

  // ── Category management ───────────────────────────────────────────────────────

  const handleCatAdd = async () => {
    const name = catInput.trim().toUpperCase();
    if (!name) { setCatErr('Enter a category name'); return; }
    if (categories.includes(name)) { setCatErr('Category already exists'); return; }
    setCatSaving(true); setCatErr('');
    try {
      const res = await aquaApi.createCategory({ name });
      if (res?.error) { setCatErr(res.error); return; }
      setCatInput('');
      await loadCategories();
    } catch (e) { setCatErr(e.message); }
    finally { setCatSaving(false); }
  };

  const handleCatDelete = async (name) => {
    if (!window.confirm(`Delete category "${name}"?\n\nProducts assigned to it will keep their category label but it won't appear in the dropdown.`)) return;
    aquaApi.getCategories().then(async rows => {
      const found = Array.isArray(rows) && rows.find(r => r.name === name);
      if (found) { await aquaApi.deleteCategory(found.id); await loadCategories(); }
    });
  };

  // ── Header helpers ────────────────────────────────────────────────────────────

  const setH = (k, v) => setHdr(h => ({ ...h, [k]: v }));

  // ── Items helpers ─────────────────────────────────────────────────────────────

  const updateItem = (idx, k, v) => {
    setItems(prev => {
      const next = prev.map((it, i) => i !== idx ? it : { ...it, [k]: v });
      const row  = next[idx];

      // Auto-set category from product selection
      if (k === 'productName') {
        const found = products.find(p => p.name === v);
        if (found) next[idx] = { ...next[idx], category: found.category };
      }

      const mrp  = parseFloat(row.mrp)      || 0;
      const frp  = parseFloat(row.frp)      || 0;
      const disc = parseFloat(row.discount) || 0;
      const nou  = parseFloat(row.nou)      || 0;

      if (k === 'frp') {
        // FRP typed → derive Disc%, recompute Amount
        if (mrp > 0) {
          const d = ((mrp - frp) / mrp) * 100;
          next[idx] = { ...next[idx], discount: d >= 0 ? d.toFixed(2) : '0.00' };
        }
        next[idx] = { ...next[idx], lineAmount: (frp * nou).toFixed(4) };

      } else if (k === 'discount') {
        // Disc% typed → derive FRP, recompute Amount
        if (mrp > 0) {
          const newFrp = mrp * (1 - disc / 100);
          next[idx] = { ...next[idx], frp: newFrp.toFixed(4), lineAmount: (newFrp * nou).toFixed(4) };
        }

      } else if (k === 'mrp') {
        // MRP changed: if FRP already set → update Disc%; if Disc% set → update FRP + Amount
        if (frp > 0 && mrp > 0) {
          const d = ((mrp - frp) / mrp) * 100;
          next[idx] = { ...next[idx], discount: d >= 0 ? d.toFixed(2) : '0.00' };
        } else if (disc > 0 && mrp > 0) {
          const newFrp = mrp * (1 - disc / 100);
          next[idx] = { ...next[idx], frp: newFrp.toFixed(4), lineAmount: (newFrp * nou).toFixed(4) };
        }

      } else if (k === 'nou') {
        // NOU changed → recompute Amount from current FRP
        next[idx] = { ...next[idx], lineAmount: (frp * nou).toFixed(4) };
      }
      // k === 'lineAmount': user typed directly, no side effects

      return next;
    });
  };

  const addItem    = () => setItems(prev => [...prev, makeItem()]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  // ── Derived totals ────────────────────────────────────────────────────────────

  const totalAmount = items.reduce((s, it) => s + (parseFloat(it.lineAmount) || 0), 0);
  const balance     = totalAmount - (parseFloat(hdr.paymentAmount) || 0);

  // ── Save / Edit / Cancel ──────────────────────────────────────────────────────

  const validItems  = items.filter(it => it.productName && it.productName.trim());
  const isFormValid = !!(
    hdr.billNo.trim() &&
    hdr.vendorName.trim() &&
    validItems.some(it => parseFloat(it.lineAmount) > 0)
  );

  const handleSpSave = async () => {
    if (!hdr.billNo.trim())    { setSpErr('Bill No is required'); return; }
    if (!hdr.vendorName.trim()) { setSpErr('Vendor / Supplier is required'); return; }
    if (!validItems.some(it => parseFloat(it.lineAmount) > 0)) { setSpErr('At least one product row with a calculated amount is required'); return; }
    setSpErr('');
    try {
      const payload = {
        entryDate:     hdr.entryDate,
        billNo:        hdr.billNo,
        vendorName:    hdr.vendorName,
        paymentAmount: hdr.paymentAmount,
        notes:         hdr.notes,
        items: validItems.map(it => ({
          productName: it.productName,
          category:    it.category || 'OTHER',
          packSize:    it.packSize,
          uom:         it.uom || 'Kgs',
          packType:    it.packType || 'BAGs',
          nou:         it.nou,
          mrp:         it.mrp,
          discount:    parseFloat(it.discount || 0) / 100,   // stored as fraction (0.05 = 5%)
          frp:         it.frp,
          lineAmount:  it.lineAmount,
        })),
      };
      if (spEditId) await aquaApi.updateStockPurchase(spEditId, payload);
      else          await aquaApi.createStockPurchase(payload);
      setHdr({ ...EMPTY_HDR });
      setItems([makeItem()]);
      setSpEditId(null);
      setTab('list');
      loadPurchases();
      loadStock();
    } catch (e) { setSpErr(e.message); }
  };

  const handleSpEdit = (r) => {
    setHdr({
      entryDate:     r.entry_date     || '',
      billNo:        r.bill_no        || '',
      vendorName:    r.vendor_name    || '',
      paymentAmount: r.payment_amount != null ? String(r.payment_amount) : '0',
      notes:         r.notes          || '',
    });

    const backendItems = Array.isArray(r.items) && r.items.length > 0 ? r.items : null;

    if (backendItems) {
      // New-style: items from chmr_stock_purchase_items
      setItems(backendItems.map(it => ({
        productName: it.product_name || '',
        category:    it.category     || 'OTHER',
        packSize:    it.pack_size    != null ? String(it.pack_size)    : '',
        uom:         it.uom          || 'Kgs',
        packType:    it.pack_type    || 'BAGs',
        nou:         it.nou          != null ? String(it.nou)          : '',
        mrp:         it.mrp          != null ? String(it.mrp)          : '',
        discount:    it.discount     != null ? (parseFloat(it.discount) * 100).toFixed(2) : '0',
        frp:         it.frp          != null ? String(it.frp)          : '',
        lineAmount:  it.line_amount  != null ? String(it.line_amount)  : '',
      })));
    } else {
      // Old-style backward compat: synthesize single item from header columns
      setItems([{
        productName: r.product_name || '',
        category:    r.category     || 'OTHER',
        packSize:    r.pack_size    != null ? String(r.pack_size)    : '',
        uom:         r.uom          || 'Kgs',
        packType:    r.pack_type    || 'BAGs',
        nou:         r.nou          != null ? String(r.nou)          : '',
        mrp:         r.mrp          != null ? String(r.mrp)          : '',
        discount:    r.discount     != null ? (parseFloat(r.discount) * 100).toFixed(2) : '0',
        frp:         r.frp          != null ? String(r.frp)          : '',
        lineAmount:  r.purchase_amount != null ? String(r.purchase_amount) : '',
      }]);
    }

    setSpEditId(r.id);
    setTab('form');
  };

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Product name CRUD ─────────────────────────────────────────────────────────

  const handlePnSave = async () => {
    const name = pnForm.name.trim();
    if (!name) { setPnErr('Name is required'); return; }
    setPnSaving(true); setPnErr('');
    try {
      const res = pnEditId
        ? await aquaApi.updateProductName(pnEditId, { name, category: pnForm.category })
        : await aquaApi.createProductName({ name, category: pnForm.category });
      if (res?.error) { setPnErr(res.error); return; }
      setPnForm({ name: '', category: 'FEED' }); setPnEditId(null);
      await loadProducts();
    } catch (e) { setPnErr(e.message); }
    finally { setPnSaving(false); }
  };

  const handlePnEdit   = (p) => { setPnForm({ name: p.name, category: p.category || 'OTHER' }); setPnEditId(p.id); setPnErr(''); };
  const handlePnCancel = ()  => { setPnForm({ name: '', category: 'FEED' }); setPnEditId(null); setPnErr(''); };
  const handlePnDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await aquaApi.deleteProductName(id); await loadProducts();
  };

  // ── Derived ledger rows ───────────────────────────────────────────────────────

  const shown = spRows.filter(r => {
    if (filterCat === 'ALL') return true;
    // Check new-style items
    if (Array.isArray(r.items) && r.items.length > 0)
      return r.items.some(it => it.category === filterCat);
    // Old-style header category
    return r.category === filterCat;
  });

  const totPurch = shown.reduce((s, r) => s + parseFloat(r.purchase_amount || 0), 0);
  const totPaid  = shown.reduce((s, r) => s + parseFloat(r.payment_amount  || 0), 0);

  // ── Compact cell style for items table ────────────────────────────────────────

  const cellStyle = { padding: '4px 6px', verticalAlign: 'middle' };
  const inputStyle = { padding: '3px 6px', fontSize: 12, height: 28, boxSizing: 'border-box', width: '100%' };
  const roInputStyle = { ...inputStyle, background: 'rgba(19,115,51,.10)', color: '#137333', fontWeight: 700, cursor: 'default' };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'stock'    ? ' active' : ''}`} onClick={() => { setTab('stock'); loadStock(); }}>📦 Stock Balance</button>
        <button className={`tab${tab === 'list'     ? ' active' : ''}`} onClick={() => setTab('list')}>🛒 Purchases</button>
        {canWrite && (
          <button className={`tab${tab === 'form' ? ' active' : ''}`}
            onClick={() => { setHdr({ ...EMPTY_HDR }); setItems([makeItem()]); setSpEditId(null); setSpErr(''); setTab('form'); }}>
            {spEditId ? '✏️ Edit Purchase' : '➕ Add Purchase'}
          </button>
        )}
        {canWrite && (
          <button className="tab" onClick={openIntAdd}>
            💹 Add Interest
          </button>
        )}
        {canWrite && <button className={`tab${tab === 'products' ? ' active' : ''}`} onClick={() => setTab('products')}>🏷️ Products</button>}
        <button className={`tab${tab === 'costimpact' ? ' active' : ''}`} onClick={() => setTab('costimpact')}>📊 Cost Impact</button>
        <button className={`tab${tab === 'pursum'    ? ' active' : ''}`} onClick={() => setTab('pursum')}>📋 Purchase Summary</button>
      </div>

      {/* ── Stock Balance ─────────────────────────────────────────────────────── */}
      {tab === 'stock' && (
        <div>
          {stockLoading ? <div className="loading-msg">Loading stock…</div> : (
            <div className="card">
              <div className="card-header">
                <div className="card-title">📦 Owner Stock Balance</div>
                <button onClick={loadStock} style={{ padding: '5px 14px', borderRadius: 20, border: '1px solid #e8eaed', background: '#fff', cursor: 'pointer', fontSize: 13 }}>↻ Refresh</button>
              </div>
              <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Product</th><th>Category</th><th>UOM</th>
                      <th style={{ textAlign: 'right' }}>Purchased</th>
                      <th style={{ textAlign: 'right' }}>Chem Used</th>
                      <th style={{ textAlign: 'right' }}>Feed Used</th>
                      <th style={{ textAlign: 'right' }}>Balance</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stock.length === 0 && (
                      <tr><td colSpan={8} style={{ textAlign: 'center', color: '#888', padding: 32 }}>
                        No purchases yet — add purchases to track stock
                      </td></tr>
                    )}
                    {stock.map((r, i) => {
                      const bal = parseFloat(r.balance_qty || 0);
                      const cat = catColor(r.category, categories);
                      return (
                        <tr key={i}>
                          <td style={{ fontWeight: 600 }}>{r.product_name}</td>
                          <td><span style={{ background: cat.bg, color: cat.color, borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{r.category}</span></td>
                          <td>{r.uom}</td>
                          <td style={{ textAlign: 'right', color: '#1a73e8' }}>{fmtQty(r.purchased_qty, r.uom)}</td>
                          <td style={{ textAlign: 'right', color: '#7b1fa2' }}>{fmtQty(r.chem_used, r.uom)}</td>
                          <td style={{ textAlign: 'right', color: '#e37400' }}>{fmtQty(r.feed_used, r.uom)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 800, fontSize: 15, color: bal <= 0 ? '#c5221f' : bal < 50 ? '#e37400' : '#137333' }}>
                            {fmtQty(bal, r.uom)}
                          </td>
                          <td>
                            {bal <= 0
                              ? <span style={{ background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>OUT OF STOCK</span>
                              : bal < 50
                              ? <span style={{ background: '#fef3cd', color: '#e37400', borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>LOW STOCK</span>
                              : <span style={{ background: '#e6f4ea', color: '#137333', borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>IN STOCK</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Purchase Ledger ───────────────────────────────────────────────────── */}
      {tab === 'list' && (
        <div>
          {/* Category filter pills */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {['ALL', ...categories].map(cat => (
              <button key={cat} onClick={() => setFilterCat(cat)}
                style={{ padding: '4px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12,
                  background: filterCat === cat ? '#1a73e8' : '#f1f3f4',
                  color: filterCat === cat ? '#fff' : '#5f6368' }}>
                {cat}
              </button>
            ))}
          </div>

          {/* Stat cards */}
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="stat-icon blue" style={{ fontSize: 22 }}>🛒</div>
              <div><div className="stat-label">Invoices</div><div className="stat-value">{shown.length}</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon orange" style={{ fontSize: 22 }}>💰</div>
              <div><div className="stat-label">Total Purchase</div><div className="stat-value">₹{fmtMoney(totPurch)}</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon green" style={{ fontSize: 22 }}>✅</div>
              <div><div className="stat-label">Total Paid</div><div className="stat-value">₹{fmtMoney(totPaid)}</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon red" style={{ fontSize: 22 }}>⏳</div>
              <div><div className="stat-label">Balance Due</div><div className="stat-value">₹{fmtMoney(totPurch - totPaid)}</div></div>
            </div>
          </div>

          <div className="card">
            {/* Interest Modal */}
            {intOpen && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={e => { if (e.target === e.currentTarget) setIntOpen(false); }}>
                <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 400,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 18, color: '#202124' }}>{intEditId ? '✏️ Edit Interest Charge' : '💹 Add Interest Charge'}</div>
                  {intErr && <div style={{ background: '#fce8e6', color: '#c5221f', borderRadius: 6,
                    padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>⚠️ {intErr}</div>}

                  <div className="form-group">
                    <label className="form-label">Date <span style={{ color: '#c5221f' }}>*</span></label>
                    <DateInput className="form-control" value={intForm.interestDate}
                      onChange={e => setIntForm(f => ({ ...f, interestDate: e.target.value }))} />
                  </div>

                  <div className="form-group" style={{ marginTop: 12 }}>
                    <label className="form-label">Vendor / Supplier <span style={{ color: '#c5221f' }}>*</span></label>
                    <select className="form-control" value={intForm.vendorName}
                      onChange={e => setIntForm(f => ({ ...f, vendorName: e.target.value }))}
                      style={!intForm.vendorName ? {} : {}}>
                      <option value="">— Select Supplier —</option>
                      {/* Unique vendor names from existing purchases */}
                      {[...new Set([
                        ...spRows.filter(r => r.vendor_name).map(r => r.vendor_name),
                        ...suppliers.map(s => s.supplier_name)
                      ])].sort().map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>

                  <div className="form-group" style={{ marginTop: 12 }}>
                    <label className="form-label">Interest Amount (₹) <span style={{ color: '#c5221f' }}>*</span></label>
                    <input className="form-control" type="number" step="0.01" min="0"
                      value={intForm.amount} placeholder="0.00"
                      onChange={e => setIntForm(f => ({ ...f, amount: e.target.value }))}
                      style={!(parseFloat(intForm.amount) > 0) ? {} : {}} />
                  </div>

                  <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                    <button className="btn btn-primary" onClick={handleIntSave} disabled={intSaving}>
                      {intSaving ? 'Saving…' : intEditId ? '💾 Update Interest' : '💾 Save Interest'}
                    </button>
                    <button className="btn btn-secondary" onClick={() => setIntOpen(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              {spLoading ? <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>Loading…</div> : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 28 }}></th>
                      <th>Date</th>
                      <th>Bill No</th>
                      <th>Vendor</th>
                      <th>Items</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th style={{ textAlign: 'right' }}>Paid</th>
                      <th style={{ textAlign: 'right' }}>Balance</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.length === 0 && (
                      <tr><td colSpan={9} style={{ textAlign: 'center', color: '#888', padding: 32 }}>No purchase records</td></tr>
                    )}
                    {shown.map(r => {
                      const bal = parseFloat(r.purchase_amount || 0) - parseFloat(r.payment_amount || 0);
                      const isExpanded = expanded.has(r.id);
                      const rowItems = Array.isArray(r.items) && r.items.length > 0 ? r.items : null;
                      const itemCount = rowItems ? rowItems.length : (r.product_name ? 1 : 0);
                      return [
                        <tr key={r.id}>
                          <td style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none', fontSize: 11 }}
                            onClick={() => toggleExpand(r.id)}>
                            {isExpanded ? '▼' : '▶'}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.entry_date)}</td>
                          <td style={{ fontSize: 12, color: '#888' }}>{r.bill_no || '—'}</td>
                          <td style={{ fontSize: 12 }}>{r.vendor_name || '—'}</td>
                          <td>
                            <span style={{ background: '#e8f0fe', color: '#1a73e8', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                              {itemCount} {itemCount === 1 ? 'item' : 'items'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>₹{fmtMoney(r.purchase_amount)}</td>
                          <td style={{ textAlign: 'right', color: '#137333', fontWeight: 600 }}>₹{fmtMoney(r.payment_amount)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: bal > 0 ? '#c5221f' : '#137333' }}>₹{fmtMoney(bal)}</td>
                          <td>
                            {canWrite && <button className="btn-sm btn-secondary" onClick={() => handleSpEdit(r)} style={{ marginRight: 4 }}>Edit</button>}
                            {canDelete && <button className="btn-sm btn-danger" onClick={async () => { if (window.confirm('Delete this invoice and all its items?')) { await aquaApi.deleteStockPurchase(r.id); loadPurchases(); loadStock(); } }}>Del</button>}
                          </td>
                        </tr>,
                        isExpanded && rowItems && (
                          <tr key={`${r.id}-items`}>
                            <td colSpan={9} style={{ padding: 0, background: '#fafbfc' }}>
                              <div style={{ borderLeft: '3px solid #1a73e8', marginLeft: 28, marginRight: 12, marginTop: 4, marginBottom: 8 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                  <thead>
                                    <tr style={{ background: '#e8f0fe' }}>
                                      <th style={{ padding: '4px 10px', textAlign: 'left', fontWeight: 700 }}>Product</th>
                                      <th style={{ padding: '4px 10px', textAlign: 'left', fontWeight: 700 }}>Category</th>
                                      <th style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 700 }}>Pack × UOM</th>
                                      <th style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 700 }}>NOU</th>
                                      <th style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 700 }}>FRP</th>
                                      <th style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 700 }}>Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rowItems.map((it, idx) => {
                                      const cat = catColor(it.category, categories);
                                      return (
                                        <tr key={idx} style={{ borderBottom: '1px solid #e8eaed' }}>
                                          <td style={{ padding: '4px 10px', fontWeight: 600 }}>{it.product_name}</td>
                                          <td style={{ padding: '4px 10px' }}>
                                            <span style={{ background: cat.bg, color: cat.color, borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{it.category}</span>
                                          </td>
                                          <td style={{ padding: '4px 10px', textAlign: 'right', color: '#5f6368' }}>
                                            {it.pack_size ? `${it.pack_size} ${it.uom}` : it.uom || '—'}
                                          </td>
                                          <td style={{ padding: '4px 10px', textAlign: 'right' }}>{it.nou || '—'}</td>
                                          <td style={{ padding: '4px 10px', textAlign: 'right' }}>₹{fmtMoney(it.frp)}</td>
                                          <td style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 700 }}>₹{fmtMoney(it.line_amount)}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        ),
                        isExpanded && !rowItems && r.product_name && (
                          <tr key={`${r.id}-legacy`}>
                            <td colSpan={9} style={{ padding: 0, background: '#fafbfc' }}>
                              <div style={{ borderLeft: '3px solid #fbbc04', marginLeft: 28, marginRight: 12, marginTop: 4, marginBottom: 8, paddingLeft: 12, paddingTop: 6, paddingBottom: 6, color: '#5f6368', fontSize: 12 }}>
                                <strong>{r.product_name}</strong>
                                {r.category && <span style={{ marginLeft: 8, background: catColor(r.category, categories).bg, color: catColor(r.category, categories).color, borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{r.category}</span>}
                                {r.pack_size && <span style={{ marginLeft: 10 }}>{r.pack_size} {r.uom}</span>}
                                {r.nou && <span style={{ marginLeft: 10 }}>NOU: {r.nou}</span>}
                                {r.frp && <span style={{ marginLeft: 10 }}>FRP: ₹{fmtMoney(r.frp)}</span>}
                              </div>
                            </td>
                          </tr>
                        ),
                      ].filter(Boolean);
                    })}
                  </tbody>
                  {shown.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>TOTAL</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>₹{fmtMoney(totPurch)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#137333' }}>₹{fmtMoney(totPaid)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: (totPurch - totPaid) > 0 ? '#c5221f' : '#137333' }}>₹{fmtMoney(totPurch - totPaid)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}
            </div>
          </div>

          {/* Interest Charges table */}
          {interest.length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card-header">
                <div className="card-title">💹 Interest Charges</div>
                <div style={{ fontSize: 13, color: '#c5221f', fontWeight: 700 }}>
                  Total: ₹{fmtMoney(interest.reduce((s, r) => s + parseFloat(r.amount || 0), 0))}
                </div>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                <table className="data-table">
                  <thead>
                    <tr><th>Date</th><th>Vendor / Supplier</th><th style={{ textAlign: 'right' }}>Interest (₹)</th>{canWrite && <th>Actions</th>}</tr>
                  </thead>
                  <tbody>
                    {interest.map(r => (
                      <tr key={r.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.interest_date)}</td>
                        <td style={{ fontWeight: 600 }}>{r.vendor_name || '—'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#c5221f' }}>₹{fmtMoney(r.amount)}</td>
                        {canWrite && (
                          <td>
                            <button className="btn-sm btn-secondary" onClick={() => openIntEdit(r)} style={{ marginRight: 4 }}>Edit</button>
                            {canDelete && (
                              <button className="btn-sm btn-danger" onClick={async () => {
                                if (window.confirm('Delete this interest entry?')) {
                                  await aquaApi.deletePurchaseInterest(r.id); loadInterest();
                                }
                              }}>Del</button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Add / Edit Purchase form ──────────────────────────────────────────── */}
      {tab === 'form' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">{spEditId ? '✏️ Edit Purchase' : '➕ Add Purchase'}</div>
          </div>
          <div className="card-body">
            {spErr && <div className="alert alert-error" style={{ marginBottom: 14 }}>{spErr}</div>}

            {/* Header: Date | Bill No | Vendor */}
            <div className="aq-form-3hdr" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 14, marginBottom: 18 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Date</label>
                <DateInput className="form-control" value={hdr.entryDate} onChange={e => setH('entryDate', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Bill No <span style={{ color: '#c5221f' }}>*</span></label>
                <input className="form-control" value={hdr.billNo} onChange={e => setH('billNo', e.target.value)}
                  placeholder="Invoice / Bill number"
                  style={!hdr.billNo.trim() ? {} : {}} />
                {!hdr.billNo.trim() && <div style={{ fontSize: 11, color: '#c5221f', marginTop: 2 }}>Required</div>}
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Vendor / Supplier <span style={{ color: '#c5221f' }}>*</span></label>
                <input className="form-control" list="supplier-list"
                  value={hdr.vendorName} onChange={e => setH('vendorName', e.target.value)}
                  placeholder={suppliers.length ? 'Select or type supplier name' : 'Supplier name'}
                  style={!hdr.vendorName.trim() ? {} : {}} />
                {!hdr.vendorName.trim() && <div style={{ fontSize: 11, color: '#c5221f', marginTop: 2 }}>Required</div>}
                <datalist id="supplier-list">
                  {suppliers.map(s => <option key={s.id} value={s.supplier_name} />)}
                </datalist>
              </div>
            </div>

            {/* Items inline table */}
            <div style={{ marginBottom: 6, fontWeight: 700, fontSize: 13, color: '#3c4043' }}>📋 Line Items</div>
            <div style={{ overflowX: 'auto', marginBottom: 0 }}>
              <table style={{ borderCollapse: 'collapse', minWidth: 1200, width: '100%', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f1f3f4' }}>
                    <th style={{ ...cellStyle, width: 30, textAlign: 'center' }}>#</th>
                    <th style={{ ...cellStyle, minWidth: 170 }}>Product <span style={{ color: '#c5221f' }}>*</span></th>
                    <th style={{ ...cellStyle, width: 110 }}>Category</th>
                    <th style={{ ...cellStyle, width: 70, textAlign: 'right' }}>Pack</th>
                    <th style={{ ...cellStyle, width: 70 }}>UOM</th>
                    <th style={{ ...cellStyle, width: 80 }}>Pack Type</th>
                    <th style={{ ...cellStyle, width: 70, textAlign: 'right' }}>NOU</th>
                    <th style={{ ...cellStyle, width: 90, textAlign: 'right' }}>MRP</th>
                    <th style={{ ...cellStyle, width: 80, textAlign: 'right' }}>Disc%</th>
                    <th style={{ ...cellStyle, width: 90, textAlign: 'right' }}>FRP</th>
                    <th style={{ ...cellStyle, width: 100, textAlign: 'right' }}>Amount</th>
                    <th style={{ ...cellStyle, width: 30 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #e8eaed' }}>
                      <td style={{ ...cellStyle, textAlign: 'center', color: '#888', fontWeight: 700 }}>{idx + 1}</td>
                      <td style={cellStyle}>
                        <select className="form-control" style={{ ...inputStyle}}
                          value={it.productName} onChange={e => updateItem(idx, 'productName', e.target.value)}>
                          <option value="">— Select —</option>
                          {products.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                        </select>
                      </td>
                      <td style={cellStyle}>
                        <select className="form-control" style={inputStyle}
                          value={it.category} onChange={e => updateItem(idx, 'category', e.target.value)}>
                          {categories.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </td>
                      <td style={cellStyle}>
                        <input className="form-control" style={{ ...inputStyle, textAlign: 'right' }}
                          value={it.packSize} onChange={e => updateItem(idx, 'packSize', e.target.value)}
                          placeholder="25" type="number" min="0" />
                      </td>
                      <td style={cellStyle}>
                        <select className="form-control" style={inputStyle}
                          value={it.uom} onChange={e => updateItem(idx, 'uom', e.target.value)}>
                          {UOMS.map(u => <option key={u}>{u}</option>)}
                        </select>
                      </td>
                      <td style={cellStyle}>
                        <select className="form-control" style={inputStyle}
                          value={it.packType} onChange={e => updateItem(idx, 'packType', e.target.value)}>
                          {PACK_TYPES.map(p => <option key={p}>{p}</option>)}
                        </select>
                      </td>
                      <td style={cellStyle}>
                        <input className="form-control" style={{ ...inputStyle, textAlign: 'right' }}
                          type="number" min="0" value={it.nou}
                          onChange={e => updateItem(idx, 'nou', e.target.value)} placeholder="0" />
                      </td>
                      <td style={cellStyle}>
                        <input className="form-control" style={{ ...inputStyle, textAlign: 'right' }}
                          type="number" step="0.01" min="0" value={it.mrp}
                          onChange={e => updateItem(idx, 'mrp', e.target.value)} placeholder="0.00" />
                      </td>
                      <td style={cellStyle}>
                        <input className="form-control" style={{ ...inputStyle, textAlign: 'right' }}
                          type="number" step="0.01" min="0" max="100"
                          value={it.discount}
                          onChange={e => updateItem(idx, 'discount', e.target.value)}
                          placeholder="0" />
                      </td>
                      <td style={cellStyle}>
                        <input className="form-control" style={{ ...inputStyle, textAlign: 'right' }}
                          type="number" step="0.01" min="0" value={it.frp}
                          onChange={e => updateItem(idx, 'frp', e.target.value)} placeholder="0.00" />
                      </td>
                      <td style={cellStyle}>
                        <input className="form-control" style={{ ...inputStyle, textAlign: 'right' }}
                          type="number" step="0.01" min="0" value={it.lineAmount}
                          onChange={e => updateItem(idx, 'lineAmount', e.target.value)} placeholder="0.00" />
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'center' }}>
                        {items.length > 1 && (
                          <button onClick={() => removeItem(idx)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c5221f', fontWeight: 900, fontSize: 16, lineHeight: 1, padding: '0 4px' }}
                            title="Remove row">×</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Add row button */}
            <div style={{ marginTop: 8, marginBottom: 14 }}>
              <button onClick={addItem}
                style={{ padding: '5px 16px', borderRadius: 6, border: '1px dashed #1a73e8', background: '#e8f0fe', color: '#1a73e8', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                + Add Row
              </button>
            </div>

            {/* Footer: Total | Payment | Balance | Notes */}
            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '14px 16px', border: '1px solid #e8eaed', display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, color: '#5f6368', fontWeight: 600 }}>Total:</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: '#137333' }}>₹{fmtMoney(totalAmount)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: 13, color: '#5f6368', fontWeight: 600, whiteSpace: 'nowrap' }}>Payment (₹):</label>
                <input className="form-control" type="number" step="0.01" min="0"
                  style={{ width: 120, padding: '4px 8px', fontSize: 13 }}
                  value={hdr.paymentAmount} onChange={e => setH('paymentAmount', e.target.value)} placeholder="0.00" />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, color: '#5f6368', fontWeight: 600 }}>Balance:</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: balance > 0 ? '#c5221f' : '#137333' }}>₹{fmtMoney(balance)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200 }}>
                <label style={{ fontSize: 13, color: '#5f6368', fontWeight: 600, whiteSpace: 'nowrap' }}>Notes:</label>
                <input className="form-control" style={{ flex: 1, padding: '4px 8px', fontSize: 13 }}
                  value={hdr.notes} onChange={e => setH('notes', e.target.value)} placeholder="Optional notes" />
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleSpSave} disabled={!isFormValid}
                style={{ opacity: isFormValid ? 1 : 0.45, cursor: isFormValid ? 'pointer' : 'not-allowed' }}
                title={!isFormValid ? 'Fill Bill No, Supplier, and at least one product row with amount' : ''}>
                {spEditId ? '💾 Update' : '➕ Save Purchase'}
              </button>
              <button className="btn btn-secondary" onClick={() => { setTab('list'); setSpEditId(null); setSpErr(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Purchase Summary tab ─────────────────────────────────────────────── */}
      {tab === 'pursum' && (() => {
        // Group purchases by vendor, sorted by total descending
        const vendorMap = new Map();
        [...spRows].sort((a, b) => (a.entry_date || '').localeCompare(b.entry_date || '')).forEach(r => {
          const v = (r.vendor_name || 'OTHERS').toUpperCase();
          if (!vendorMap.has(v)) vendorMap.set(v, []);
          vendorMap.get(v).push(r);
        });
        const vendors = Array.from(vendorMap.entries()).sort(([, a], [, b]) =>
          b.reduce((s, r) => s + parseFloat(r.purchase_amount || 0), 0) -
          a.reduce((s, r) => s + parseFloat(r.purchase_amount || 0), 0)
        );

        // Grand totals
        const grandPurch = spRows.reduce((s, r) => s + parseFloat(r.purchase_amount || 0), 0);
        const grandPaid  = spRows.reduce((s, r) => s + parseFloat(r.payment_amount  || 0), 0);

        // Category totals from all items
        const catTotals = {};
        spRows.forEach(r => {
          const its = Array.isArray(r.items) && r.items.length > 0 ? r.items : [];
          if (its.length > 0) {
            its.forEach(it => {
              const c = (it.category || 'OTHER').toUpperCase();
              catTotals[c] = (catTotals[c] || 0) + parseFloat(it.line_amount || 0);
            });
          } else if (r.category) {
            const c = r.category.toUpperCase();
            catTotals[c] = (catTotals[c] || 0) + parseFloat(r.purchase_amount || 0);
          }
        });

        // Per-vendor totals for the header summary bar
        const topVendors = vendors.slice(0, 3);
        const othersTotal = vendors.slice(3).reduce((s, [, bills]) =>
          s + bills.reduce((ss, r) => ss + parseFloat(r.purchase_amount || 0), 0), 0);

        // Number formatters
        const fN  = (v) => { const n = parseFloat(v || 0); return n === 0 ? '-' : n.toLocaleString('en-IN', { maximumFractionDigits: 0 }); };
        const fNF = (v) => { const n = parseFloat(v || 0); return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
        const fP  = (v) => { const n = parseFloat(v || 0); return n > 0 ? (n * 100).toFixed(2) + '%' : '0.00%'; };
        const fD  = (d) => { if (!d) return ''; const [y, m, dd] = d.split('-'); return `${dd}.${m}.${y ? y.slice(2) : ''}`; };

        // Shared border
        const B  = '1px solid #c0c4cc';
        const B2 = '2px solid #3d3b8e';
        const th = (extra = {}) => ({ padding: '6px 8px', border: B, fontWeight: 700, fontSize: 11, textAlign: 'center', whiteSpace: 'nowrap', ...extra });
        const td = (extra = {}) => ({ padding: '5px 8px', border: B, fontSize: 12, verticalAlign: 'middle', ...extra });

        return (
          <div style={{ overflowX: 'auto', fontFamily: 'Arial, sans-serif' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <tbody>

                {/* ── Title header ── */}
                <tr>
                  <td colSpan={8} style={{ background: '#3d3b8e', padding: '12px 18px', border: B2 }}>
                    <span style={{ color: '#fff', fontSize: 20, fontWeight: 900, fontStyle: 'italic', letterSpacing: 1 }}>
                      Chemical Summary
                    </span>
                    <span style={{ color: '#ffd54f', fontSize: 16, fontWeight: 700, marginLeft: 10 }}>
                      — Purchases
                    </span>
                    <span style={{ float: 'right', color: '#fff', fontWeight: 900, fontSize: 22 }}>
                      ₹ {fN(grandPurch)}
                    </span>
                  </td>
                </tr>

                {/* ── NET PURCHASE summary bar ── */}
                <tr style={{ background: '#e8e4f0' }}>
                  <td colSpan={2} style={th({ background: '#4a4880', color: '#fff', fontSize: 13 })}>NET PURCHASE</td>
                  <td colSpan={2} style={th({ background: '#4a4880', color: '#fff' })}>₹</td>
                  <td colSpan={2} style={th({ background: '#4a4880', color: '#ffd54f', fontSize: 15 })}>{fN(grandPurch)}</td>
                  <td style={th({ background: '#2e7d32', color: '#fff' })}>PAID</td>
                  <td style={th({ background: '#c5221f', color: '#fff' })}>BALANCE</td>
                </tr>
                <tr style={{ background: '#f5f3ff' }}>
                  <td colSpan={4} style={td({ textAlign: 'center', fontWeight: 700, color: '#3d3b8e', fontSize: 13 })}>
                    Total Invoices: {spRows.length}
                  </td>
                  <td style={td({ textAlign: 'right', fontWeight: 800, color: '#1a237e', fontSize: 15 })}>₹ {fNF(grandPurch)}</td>
                  <td style={td()}></td>
                  <td style={td({ textAlign: 'right', fontWeight: 700, color: '#2e7d32' })}>₹ {fNF(grandPaid)}</td>
                  <td style={td({ textAlign: 'right', fontWeight: 800, color: grandPurch - grandPaid > 0 ? '#c5221f' : '#2e7d32', fontSize: 14 })}>
                    ₹ {fNF(grandPurch - grandPaid)}
                  </td>
                </tr>

                {/* ── Vendor quick-summary row ── */}
                {vendors.length > 0 && (
                  <tr style={{ background: '#e8e4f0' }}>
                    {topVendors.map(([vendor, bills]) => {
                      const tot = bills.reduce((s, r) => s + parseFloat(r.purchase_amount || 0), 0);
                      return (
                        <td key={vendor} colSpan={2} style={td({ textAlign: 'center', fontWeight: 700, color: '#3d3b8e', fontSize: 12 })}>
                          {vendor}<br/>
                          <span style={{ color: '#c5221f', fontSize: 14, fontWeight: 800 }}>₹ {fN(tot)}</span>
                        </td>
                      );
                    })}
                    {vendors.length > 3 && (
                      <td colSpan={8 - topVendors.length * 2} style={td({ textAlign: 'center', fontWeight: 700, color: '#555', fontSize: 12 })}>
                        OTHERS<br/><span style={{ color: '#c5221f', fontSize: 14, fontWeight: 800 }}>₹ {fN(othersTotal)}</span>
                      </td>
                    )}
                  </tr>
                )}

                {/* ── Category totals grid ── */}
                {Object.keys(catTotals).length > 0 && (() => {
                  const catEntries = Object.entries(catTotals).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
                  const rows = [];
                  for (let i = 0; i < catEntries.length; i += 4) {
                    rows.push(catEntries.slice(i, i + 4));
                  }
                  return rows.map((row, ri) => (
                    <tr key={ri} style={{ background: ri % 2 === 0 ? '#f0f0ff' : '#f8f7ff' }}>
                      {row.map(([cat, tot]) => {
                        const cc = catColor(cat, categories);
                        return (
                          <td key={cat} colSpan={2} style={{ ...td(), textAlign: 'center', padding: '8px 10px' }}>
                            <div style={{ fontWeight: 700, fontSize: 11, color: cc.color, background: cc.bg, borderRadius: 8, padding: '2px 8px', display: 'inline-block', marginBottom: 2 }}>{cat}</div>
                            <div style={{ fontWeight: 900, fontSize: 16, color: '#1a237e' }}>₹ {fN(tot)}</div>
                          </td>
                        );
                      })}
                      {/* Pad remaining */}
                      {Array(4 - row.length).fill(0).map((_, i) => <td key={`pad-${i}`} colSpan={2} style={td()}></td>)}
                    </tr>
                  ));
                })()}

                <tr><td colSpan={8} style={{ height: 10, background: '#fff' }}></td></tr>

                {/* ── Per-vendor sections ── */}
                {spRows.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: '#888', padding: 48, fontSize: 15 }}>No purchases recorded</td></tr>
                )}

                {vendors.map(([vendor, bills], vi) => {
                  const vTot  = bills.reduce((s, r) => s + parseFloat(r.purchase_amount || 0), 0);
                  const vPaid = bills.reduce((s, r) => s + parseFloat(r.payment_amount  || 0), 0);
                  const vBal  = vTot - vPaid;

                  return [
                    /* Supplier header */
                    <tr key={`vh-${vi}`} style={{ background: '#3d3b8e' }}>
                      <td colSpan={5} style={{ ...td(), background: '#3d3b8e', color: '#fff', fontSize: 16, fontWeight: 900, fontStyle: 'italic', letterSpacing: 0.5, padding: '10px 14px' }}>
                        {vi + 1}. {vendor}
                      </td>
                      <td style={{ ...td(), background: '#3d3b8e', color: '#ffd54f', fontWeight: 800, textAlign: 'right', fontSize: 15 }}>₹ {fN(vTot)}</td>
                      <td style={{ ...td(), background: '#2e7d32', color: '#fff', fontWeight: 700, textAlign: 'right' }}>₹ {fN(vPaid)}</td>
                      <td style={{ ...td(), background: vBal > 0 ? '#c5221f' : '#2e7d32', color: '#fff', fontWeight: 700, textAlign: 'right' }}>₹ {fN(vBal)}</td>
                    </tr>,

                    /* Column headers */
                    <tr key={`vhdr-${vi}`} style={{ background: '#b8b870' }}>
                      <th style={th({ color: '#3d0000' })}>DATE</th>
                      <th style={th({ color: '#3d0000', textAlign: 'left', minWidth: 180 })}>DESCRIPTION</th>
                      <th style={th({ color: '#3d0000' })}>NOU</th>
                      <th style={th({ color: '#3d0000' })}>MRP</th>
                      <th style={th({ color: '#3d0000' })}>DISC</th>
                      <th style={th({ color: '#3d0000' })}>FRP</th>
                      <th style={th({ color: '#3d0000' })}>PURCHASES</th>
                      <th style={th({ color: '#3d0000' })}>PAYMENTS</th>
                    </tr>,

                    /* Bills */
                    ...bills.map((r, bi) => {
                      const its  = Array.isArray(r.items) && r.items.length > 0 ? r.items : [];
                      const paid = parseFloat(r.payment_amount || 0);
                      const billBg = bi % 2 === 0 ? '#fafafa' : '#f5f3ff';
                      return [
                        /* Bill header row */
                        <tr key={`bill-${r.id}`} style={{ background: '#dde0f5' }}>
                          <td style={td({ fontWeight: 700, color: '#888', fontSize: 11, whiteSpace: 'nowrap' })}>{fD(r.entry_date)}</td>
                          <td colSpan={6} style={{ ...td(), fontWeight: 700, color: '#3d3b8e', fontSize: 13 }}>
                            Bill No.{bi + 1}{r.bill_no ? ` — ${r.bill_no}` : ''}
                          </td>
                          <td style={td({ color: '#888', fontSize: 11 })}></td>
                        </tr>,

                        /* Line items */
                        ...(its.length > 0 ? its.map((it, ii) => {
                          const amt = parseFloat(it.line_amount || 0);
                          return (
                            <tr key={`it-${r.id}-${ii}`} style={{ background: billBg }}>
                              <td style={td({ color: '#888', fontSize: 11, whiteSpace: 'nowrap' })}></td>
                              <td style={{ ...td(), textAlign: 'left', fontWeight: 500 }}>
                                {(it.product_name || '—').toUpperCase()}
                                {it.pack_size ? <span style={{ color: '#888', fontSize: 10, marginLeft: 6 }}>{it.pack_size}{it.uom}</span> : null}
                              </td>
                              <td style={td({ textAlign: 'right', color: '#555' })}>{it.nou || '—'}</td>
                              <td style={td({ textAlign: 'right', color: '#555' })}>{it.mrp ? fN(it.mrp) : '—'}</td>
                              <td style={td({ textAlign: 'right', color: '#888', fontSize: 11 })}>{fP(it.discount)}</td>
                              <td style={td({ textAlign: 'right', fontWeight: 600, color: '#1a237e' })}>{it.frp ? fN(it.frp) : '—'}</td>
                              <td style={td({ textAlign: 'right', fontWeight: 700, color: amt > 0 ? '#222' : '#bbb' })}>{amt > 0 ? fN(amt) : '-'}</td>
                              <td style={td()}></td>
                            </tr>
                          );
                        }) : [
                          /* Legacy single-item row */
                          <tr key={`leg-${r.id}`} style={{ background: billBg }}>
                            <td style={td({ fontSize: 11 })}></td>
                            <td style={{ ...td(), textAlign: 'left', fontWeight: 500 }}>{r.product_name ? r.product_name.toUpperCase() : '—'}</td>
                            <td style={td({ textAlign: 'right', color: '#555' })}>{r.nou || '—'}</td>
                            <td style={td({ textAlign: 'right', color: '#555' })}>{r.mrp ? fN(r.mrp) : '—'}</td>
                            <td style={td({ textAlign: 'right', color: '#888', fontSize: 11 })}>{fP(r.discount)}</td>
                            <td style={td({ textAlign: 'right', fontWeight: 600, color: '#1a237e' })}>{r.frp ? fN(r.frp) : '—'}</td>
                            <td style={td({ textAlign: 'right', fontWeight: 700 })}>{fN(r.purchase_amount)}</td>
                            <td style={td()}></td>
                          </tr>
                        ]),

                        /* Payment row */
                        paid > 0 && (
                          <tr key={`pay-${r.id}`} style={{ background: '#e8f5e9' }}>
                            <td style={td({ fontSize: 10, color: '#888' })}></td>
                            <td colSpan={5} style={{ ...td(), color: '#2e7d32', fontWeight: 700, fontSize: 11, textAlign: 'right' }}>PAYMENT</td>
                            <td style={td({ textAlign: 'right', fontWeight: 700, color: '#2e7d32' })}>{fN(r.purchase_amount)}</td>
                            <td style={td({ textAlign: 'right', fontWeight: 800, color: '#2e7d32' })}>{fN(paid)}</td>
                          </tr>
                        ),
                      ].filter(Boolean);
                    }).flat(),

                    /* Vendor sub-total */
                    <tr key={`vtot-${vi}`} style={{ background: '#4a4880' }}>
                      <td colSpan={5} style={{ ...td(), color: '#fff', fontWeight: 800, textAlign: 'right', background: '#4a4880' }}>
                        {vendor} — TOTAL
                      </td>
                      <td style={{ ...td(), background: '#4a4880', color: '#ffd54f', fontWeight: 900, fontSize: 15, textAlign: 'right' }}>₹ {fNF(vTot)}</td>
                      <td style={{ ...td(), background: '#2e7d32', color: '#fff', fontWeight: 700, textAlign: 'right' }}>₹ {fNF(vPaid)}</td>
                      <td style={{ ...td(), background: vBal > 0 ? '#c5221f' : '#2e7d32', color: '#fff', fontWeight: 700, textAlign: 'right' }}>₹ {fNF(vBal)}</td>
                    </tr>,

                    <tr key={`vsp-${vi}`}><td colSpan={8} style={{ height: 10, background: '#fff' }}></td></tr>,
                  ];
                }).flat()}

                {/* ── Grand total footer ── */}
                {spRows.length > 0 && (
                  <tr style={{ background: '#1a237e' }}>
                    <td colSpan={5} style={{ ...td(), color: '#fff', fontWeight: 900, fontSize: 14, textAlign: 'right', background: '#1a237e' }}>GRAND TOTAL</td>
                    <td style={{ ...td(), background: '#1a237e', color: '#ffd54f', fontWeight: 900, fontSize: 16, textAlign: 'right' }}>₹ {fNF(grandPurch)}</td>
                    <td style={{ ...td(), background: '#2e7d32', color: '#fff', fontWeight: 800, textAlign: 'right' }}>₹ {fNF(grandPaid)}</td>
                    <td style={{ ...td(), background: grandPurch - grandPaid > 0 ? '#c5221f' : '#2e7d32', color: '#fff', fontWeight: 800, textAlign: 'right' }}>₹ {fNF(grandPurch - grandPaid)}</td>
                  </tr>
                )}

              </tbody>
            </table>
          </div>
        );
      })()}

      {/* ── Cost Impact tab ──────────────────────────────────────────────────── */}
      {tab === 'costimpact' && (() => {
        // Latest FRP + pack_size per product from purchase history
        const latestInfo = {}; // productName → { frp, packSize, packType, uom, date }
        [...spRows].sort((a, b) => (a.entry_date || '').localeCompare(b.entry_date || '')).forEach(r => {
          if (Array.isArray(r.items)) {
            r.items.forEach(it => {
              if (it.product_name && parseFloat(it.frp) > 0) {
                latestInfo[it.product_name] = {
                  frp:      parseFloat(it.frp),
                  packSize: parseFloat(it.pack_size || 0),
                  packType: it.pack_type || 'BAGs',
                  uom:      it.uom || 'Kgs',
                };
              }
            });
          }
        });

        // Ponds with area
        const ponds = ciPonds; // already filtered for area > 0

        // Build product rows from owner stock
        const prodRows = stock.map(s => {
          const info  = latestInfo[s.product_name] || {};
          const rates = ciRates[s.product_name]    || { vend: '', brr: '' };
          const brr   = parseFloat(rates.brr) || 0;
          const frp   = info.frp || 0;
          const ps    = info.packSize || 0;

          const pondQtys = ponds.map(p => {
            const area = parseFloat(p.area || 0);
            return { pond: p.pond_name, area, qty: brr > 0 ? parseFloat((brr * area).toFixed(3)) : 0 };
          });
          const totKg   = parseFloat(pondQtys.reduce((s, p) => s + p.qty, 0).toFixed(3));
          const totReq  = ps > 0 && totKg > 0 ? parseFloat((totKg / ps).toFixed(3)) : 0;
          const costImp = parseFloat((totReq * frp).toFixed(2));

          return { ...s, info, rates, pondQtys, totKg, totReq, costImp, frp, packSize: ps, packType: info.packType, uom: info.uom };
        });

        const grandCost = prodRows.reduce((s, r) => s + r.costImp, 0);

        const setRate = (name, field, val) =>
          setCiRates(prev => ({ ...prev, [name]: { ...(prev[name] || { vend: '', brr: '' }), [field]: val } }));

        // Cell styles
        const B = '1px solid #d0d4da';
        const th = (extra = {}) => ({
          padding: '7px 8px', border: B, fontWeight: 700, fontSize: 11,
          textAlign: 'center', whiteSpace: 'nowrap', verticalAlign: 'middle', ...extra,
        });
        const td = (extra = {}) => ({
          padding: '6px 8px', border: B, fontSize: 12,
          verticalAlign: 'middle', textAlign: 'center', ...extra,
        });

        return (
          <div>
            {/* Info bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {ponds.map(p => (
                  <span key={p.pond_name} style={{ background: '#e8f0fe', color: '#1a237e', borderRadius: 10, padding: '4px 12px', fontSize: 13, fontWeight: 700 }}>
                    {p.pond_name}: {parseFloat(p.area).toFixed(2)} Acres
                  </span>
                ))}
              </div>
              {ponds.length === 0 && !ciLoading && (
                <span style={{ fontSize: 13, color: '#888' }}>No ponds with area configured — add acreage via Settings → Ponds</span>
              )}
              {ciLoading && <span style={{ fontSize: 13, color: '#888' }}>Loading ponds…</span>}
              {grandCost > 0 && (
                <span style={{ marginLeft: 'auto', fontWeight: 800, fontSize: 15, color: '#c5221f' }}>
                  Total Cost Impact: ₹{fmtMoney(grandCost)}
                </span>
              )}
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', minWidth: 700 }}>
                <thead>
                  {/* Section header row */}
                  <tr>
                    <th style={th({ background: '#37474f', color: '#fff', rowSpan: 2 })} rowSpan={2}>Product</th>
                    <th style={th({ background: '#37474f', color: '#fff' })} rowSpan={2}>Cat.</th>
                    <th style={th({ background: '#37474f', color: '#fff' })} rowSpan={2}>Pack<br/>({' '}Kgs)</th>
                    <th style={th({ background: '#37474f', color: '#fff' })} rowSpan={2}>FRP<br/>(₹/pack)</th>
                    <th style={th({ background: '#8d6e00', color: '#fff' })} colSpan={2}>DOSE / ACRE</th>
                    {ponds.map(p => (
                      <th key={p.pond_name} style={th({ background: '#1a237e', color: '#fff' })} colSpan={1}>
                        {p.pond_name}<br/><span style={{ fontWeight: 400, fontSize: 10 }}>{parseFloat(p.area).toFixed(2)} Ac</span>
                      </th>
                    ))}
                    <th style={th({ background: '#2e7d32', color: '#fff' })} rowSpan={2}>TOT.<br/>REQ</th>
                    <th style={th({ background: '#c5221f', color: '#fff' })} rowSpan={2}>COST<br/>IMPACT (₹)</th>
                  </tr>
                  <tr>
                    <th style={th({ background: '#b8860b', color: '#fff' })}>VEND</th>
                    <th style={th({ background: '#9e7700', color: '#fff' })}>BRR</th>
                    {ponds.map(p => (
                      <th key={p.pond_name} style={th({ background: '#283593', color: '#fff', fontSize: 10 })}>Kgs</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {prodRows.length === 0 && (
                    <tr><td colSpan={6 + ponds.length} style={{ textAlign: 'center', color: '#888', padding: 32 }}>
                      No products in stock — add purchases first
                    </td></tr>
                  )}
                  {prodRows.map((r, i) => {
                    const cat  = catColor(r.category, categories);
                    const rowBg = i % 2 === 0 ? '#fafbfc' : '#fff';
                    const hasBrr = parseFloat(r.rates.brr) > 0;
                    return (
                      <tr key={r.product_name} style={{ background: rowBg }}>
                        <td style={td({ textAlign: 'left', fontWeight: 600 })}>{r.product_name}</td>
                        <td style={td()}>
                          <span style={{ background: cat.bg, color: cat.color, borderRadius: 8, padding: '2px 7px', fontSize: 10, fontWeight: 700 }}>
                            {r.category}
                          </span>
                        </td>
                        <td style={td({ color: '#555' })}>
                          {r.packSize > 0 ? `${r.packSize} ${r.uom || ''}` : '—'}
                        </td>
                        <td style={td({ fontWeight: 700, color: '#1a73e8' })}>
                          {r.frp > 0 ? `₹${fmtMoney(r.frp)}` : '—'}
                        </td>
                        {/* VEND input */}
                        <td style={{ ...td(), padding: '4px 6px' }}>
                          <input
                            type="number" step="0.01" min="0"
                            placeholder="—"
                            value={r.rates.vend}
                            onChange={e => setRate(r.product_name, 'vend', e.target.value)}
                            style={{ width: 64, padding: '3px 6px', fontSize: 12, textAlign: 'right', border: '1px solid #d0d4da', borderRadius: 4, background: '#fffde7' }}
                          />
                        </td>
                        {/* BRR input */}
                        <td style={{ ...td(), padding: '4px 6px' }}>
                          <input
                            type="number" step="0.01" min="0"
                            placeholder="—"
                            value={r.rates.brr}
                            onChange={e => setRate(r.product_name, 'brr', e.target.value)}
                            style={{ width: 64, padding: '3px 6px', fontSize: 12, textAlign: 'right', border: '1px solid #9e7700', borderRadius: 4, background: hasBrr ? '#fff8e1' : '#fff', fontWeight: hasBrr ? 700 : 400 }}
                          />
                        </td>
                        {/* Per-pond quantities */}
                        {r.pondQtys.map(pq => (
                          <td key={pq.pond} style={td({ color: pq.qty > 0 ? '#1a237e' : '#bbb', fontWeight: pq.qty > 0 ? 700 : 400 })}>
                            {pq.qty > 0 ? pq.qty.toFixed(2) : '—'}
                          </td>
                        ))}
                        {/* TOT.REQ */}
                        <td style={td({ fontWeight: 800, color: r.totReq > 0 ? '#2e7d32' : '#bbb', fontSize: 14 })}>
                          {r.totReq > 0 ? r.totReq.toFixed(3) : '—'}
                        </td>
                        {/* COST IMPACT */}
                        <td style={td({ fontWeight: 800, color: r.costImp > 0 ? '#c5221f' : '#bbb', fontSize: 14 })}>
                          {r.costImp > 0 ? fmtMoney(r.costImp) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {grandCost > 0 && (
                  <tfoot>
                    <tr style={{ background: '#1a237e' }}>
                      <td colSpan={6 + ponds.length} style={{ padding: '9px 12px', color: '#fff', fontWeight: 800, fontSize: 13, textAlign: 'right', border: B }}>
                        TOTAL COST IMPACT
                      </td>
                      <td style={{ padding: '9px 12px', color: '#ffd54f', fontWeight: 900, fontSize: 16, textAlign: 'right', border: B, whiteSpace: 'nowrap' }}>
                        ₹{fmtMoney(grandCost)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            <div style={{ marginTop: 12, fontSize: 12, color: '#888', lineHeight: 1.7 }}>
              <strong>How to use:</strong> Enter <em>BRR</em> (your applied dose per acre) for each product. Quantities per pond are auto-calculated as BRR × pond acres.
              TOT.REQ = total kg ÷ pack size. COST IMPACT = TOT.REQ × FRP (latest purchase price).
              <br/>VEND = vendor's recommended dose/acre (reference only, does not affect calculations).
            </div>
          </div>
        );
      })()}

      {/* ── Products master ───────────────────────────────────────────────────── */}
      {tab === 'products' && (
        <div className="card">
          <div className="card-header"><div className="card-title">🏷️ Product Names Master</div></div>
          <div className="card-body">
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {/* List */}
              <div style={{ flex: 1, minWidth: 320 }}>
                <table className="data-table">
                  <thead>
                    <tr><th>#</th><th>Product Name</th><th>Category</th>{canWrite && <th>Actions</th>}</tr>
                  </thead>
                  <tbody>
                    {products.length === 0 && (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No products yet — add one →</td></tr>
                    )}
                    {products.map((p, i) => {
                      const cat = catColor(p.category, categories);
                      return (
                        <tr key={p.id} style={{ background: pnEditId === p.id ? 'rgba(26,115,232,.06)' : undefined }}>
                          <td style={{ color: '#888', fontSize: 12 }}>{i + 1}</td>
                          <td><strong>{p.name}</strong></td>
                          <td><span style={{ background: cat.bg, color: cat.color, borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{p.category}</span></td>
                          {canWrite && (
                            <td>
                              <button className="btn-sm btn-secondary" onClick={() => handlePnEdit(p)} style={{ marginRight: 4 }}>Edit</button>
                              {canDelete && <button className="btn-sm btn-danger" onClick={() => handlePnDelete(p.id)}>Del</button>}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Form */}
              {canWrite && (
                <div style={{ width: 300, flexShrink: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#202124' }}>
                    {pnEditId ? '✏️ Edit Product' : '➕ New Product'}
                  </div>
                  {pnErr && <div className="alert alert-error" style={{ marginBottom: 10, fontSize: 13 }}>{pnErr}</div>}
                  <div className="form-group">
                    <label className="form-label">Product Name <span style={{ color: '#c5221f' }}>*</span></label>
                    <input className="form-control" placeholder="e.g. CP Hi-Pro 35, EDTA, BKC"
                      value={pnForm.name} onChange={e => setPnForm(f => ({ ...f, name: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && handlePnSave()} />
                  </div>
                  <div className="form-group" style={{ marginTop: 10 }}>
                    <label className="form-label">Category</label>
                    <select className="form-control" value={pnForm.category} onChange={e => setPnForm(f => ({ ...f, category: e.target.value }))}>
                      {categories.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                    <button className="btn btn-primary" onClick={handlePnSave} disabled={pnSaving || !pnForm.name.trim()}>
                      {pnSaving ? 'Saving…' : pnEditId ? 'Update' : 'Add'}
                    </button>
                    {pnEditId && <button className="btn btn-secondary" onClick={handlePnCancel}>Cancel</button>}
                  </div>

                  {/* Categories management */}
                  <div style={{ marginTop: 28, borderTop: '1px solid #e8eaed', paddingTop: 18 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#202124' }}>📂 Manage Categories</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                      {categories.map(c => {
                        const cc = catColor(c, categories);
                        const isDefault = DEFAULT_CATEGORIES.includes(c);
                        return (
                          <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                            background: cc.bg, color: cc.color, borderRadius: 12,
                            padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                            {c}
                            {canDelete && !isDefault && (
                              <button onClick={() => handleCatDelete(c)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer',
                                  color: cc.color, fontWeight: 900, fontSize: 13, padding: '0 0 0 2px',
                                  lineHeight: 1, opacity: 0.7 }} title="Remove">×</button>
                            )}
                          </span>
                        );
                      })}
                    </div>
                    {canWrite && (
                      <div>
                        {catErr && <div style={{ fontSize: 12, color: '#c5221f', marginBottom: 6 }}>⚠️ {catErr}</div>}
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input className="form-control" style={{ flex: 1 }}
                            placeholder="New category name…"
                            value={catInput} onChange={e => { setCatInput(e.target.value); setCatErr(''); }}
                            onKeyDown={e => e.key === 'Enter' && handleCatAdd()} />
                          <button className="btn btn-primary" onClick={handleCatAdd} disabled={catSaving || !catInput.trim()}
                            style={{ whiteSpace: 'nowrap', padding: '6px 14px', fontSize: 13 }}>
                            {catSaving ? '…' : '+ Add'}
                          </button>
                        </div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                          Built-in categories (FEED, MINERAL, etc.) cannot be removed.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
